import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

type FeedbackTone = 'error' | 'warning' | 'info' | 'success';

export interface FeedbackDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  tone?: FeedbackTone;
  actionLabel?: string;
  onClose: () => void;
}

const toneStyles: Record<FeedbackTone, { badge: string; border: string; iconBg: string; iconColor: string; label: string; icon: string }> = {
  error: {
    badge: 'rgba(220,53,69,0.12)',
    border: 'rgba(220,53,69,0.22)',
    iconBg: 'rgba(220,53,69,0.14)',
    iconColor: 'var(--color-error)',
    label: 'Something needs attention',
    icon: '!',
  },
  warning: {
    badge: 'rgba(255,193,7,0.18)',
    border: 'rgba(196,160,58,0.28)',
    iconBg: 'rgba(217,178,86,0.18)',
    iconColor: '#8a6505',
    label: 'Please review this',
    icon: '!',
  },
  info: {
    badge: 'rgba(30,30,30,0.08)',
    border: 'rgba(30,30,30,0.14)',
    iconBg: 'rgba(30,30,30,0.08)',
    iconColor: 'var(--color-text)',
    label: 'Heads up',
    icon: 'i',
  },
  success: {
    badge: 'rgba(40,167,69,0.12)',
    border: 'rgba(40,167,69,0.2)',
    iconBg: 'rgba(40,167,69,0.14)',
    iconColor: 'var(--color-success)',
    label: 'Done',
    icon: '✓',
  },
};

export function FeedbackDialog({
  isOpen,
  title,
  message,
  tone = 'error',
  actionLabel = 'Got it',
  onClose,
}: FeedbackDialogProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const toneStyle = toneStyles[tone];

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const dialog = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-6)',
        backgroundColor: 'rgba(17, 17, 17, 0.42)',
        backdropFilter: 'blur(4px)',
        zIndex: 2200,
      }}
    >
      <div
        role={tone === 'error' || tone === 'warning' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(100%, 560px)',
          borderRadius: 'var(--radius-xl)',
          backgroundColor: 'var(--color-surface)',
          border: `1px solid ${toneStyle.border}`,
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--spacing-4)',
            padding: 'var(--spacing-5)',
            borderBottom: '1px solid var(--color-border-light)',
            background:
              tone === 'error'
                ? 'linear-gradient(180deg, rgba(220,53,69,0.03) 0%, rgba(220,53,69,0) 100%)'
                : 'linear-gradient(180deg, rgba(217,178,86,0.06) 0%, rgba(217,178,86,0) 100%)',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: '44px',
              height: '44px',
              flexShrink: 0,
              borderRadius: '999px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: toneStyle.iconBg,
              color: toneStyle.iconColor,
              fontFamily: 'var(--font-family-heading)',
              fontSize: '24px',
              fontWeight: 700,
            }}
          >
            {toneStyle.icon}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 10px',
                marginBottom: 'var(--spacing-3)',
                borderRadius: '999px',
                backgroundColor: toneStyle.badge,
                color: toneStyle.iconColor,
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {toneStyle.label}
            </div>
            <h2
              id={titleId}
              style={{
                margin: 0,
                fontSize: 'var(--font-size-2xl)',
                lineHeight: 1.1,
              }}
            >
              {title}
            </h2>
            <p
              id={descriptionId}
              style={{
                marginTop: 'var(--spacing-3)',
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-base)',
                lineHeight: 1.6,
              }}
            >
              {message}
            </p>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: 'var(--spacing-4) var(--spacing-5)',
            backgroundColor: 'var(--color-surface)',
          }}
        >
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            style={{
              minWidth: '132px',
              padding: '0.8rem 1.2rem',
              borderRadius: '999px',
              border: '1px solid var(--color-primary)',
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-text-inverse)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}

export default FeedbackDialog;
